import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Switch } from "./ui/switch";
import { useI18n } from "../i18n/I18nContext";
import { readConsent, saveConsent, updateConsent, applyStoredConsent } from "../lib/consent";

export default function CookieBanner() {
  const { t } = useI18n();
  const { lang } = useParams();
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      applyStoredConsent(stored);
      setAnalytics(!!stored.analytics);
      setMarketing(!!stored.marketing);
    } else {
      setVisible(true);
    }
  }, []);

  const commit = (prefs) => {
    saveConsent(prefs);
    updateConsent(prefs);
    setAnalytics(prefs.analytics);
    setMarketing(prefs.marketing);
    setVisible(false);
    setOpen(false);
  };

  const acceptAll = () => commit({ analytics: true, marketing: true });
  const rejectAll = () => commit({ analytics: false, marketing: false });
  const savePrefs = () => commit({ analytics, marketing });

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            data-testid="cookie-banner"
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:p-5"
          >
            <div className="mx-auto max-w-5xl rounded-2xl border border-ink/10 bg-white/95 backdrop-blur-md shadow-[0_20px_60px_rgba(10,25,47,0.18)] p-5 sm:p-6">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center">
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-ink text-terracotta">
                    <Cookie size={20} />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-ink">
                      {t("cookie.title")}
                    </h3>
                    <p className="mt-1 max-w-2xl text-[13px] leading-relaxed text-mineral">
                      {t("cookie.desc")}{" "}
                      <Link
                        to={`/${lang}/cookies`}
                        data-testid="cookie-banner-policy-link"
                        className="font-medium text-terracotta underline underline-offset-2"
                      >
                        {t("cookie.more")}
                      </Link>
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2.5 lg:ml-auto lg:shrink-0">
                  <button
                    type="button"
                    data-testid="cookie-manage"
                    onClick={() => setOpen(true)}
                    className="rounded-full border border-ink/15 px-4 py-2.5 text-sm font-medium text-ink hover:bg-ink/5 transition-colors"
                  >
                    {t("cookie.manage")}
                  </button>
                  <button
                    type="button"
                    data-testid="cookie-reject-all"
                    onClick={rejectAll}
                    className="rounded-full border border-ink/15 px-4 py-2.5 text-sm font-medium text-ink hover:bg-ink/5 transition-colors"
                  >
                    {t("cookie.rejectAll")}
                  </button>
                  <button
                    type="button"
                    data-testid="cookie-accept-all"
                    onClick={acceptAll}
                    className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-terracotta transition-colors"
                  >
                    {t("cookie.acceptAll")}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cookie-preferences-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-ink">{t("cookie.prefTitle")}</DialogTitle>
            <DialogDescription>{t("cookie.prefDesc")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 rounded-xl border border-ink/10 bg-sand p-4">
              <div>
                <p className="text-sm font-semibold text-ink">{t("cookie.necessary")}</p>
                <p className="mt-1 text-xs text-mineral">{t("cookie.necessaryDesc")}</p>
              </div>
              <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-terracotta">
                {t("cookie.always")}
              </span>
            </div>

            <div className="flex items-start justify-between gap-4 rounded-xl border border-ink/10 p-4">
              <div>
                <p className="text-sm font-semibold text-ink">{t("cookie.analytics")}</p>
                <p className="mt-1 text-xs text-mineral">{t("cookie.analyticsDesc")}</p>
              </div>
              <Switch
                data-testid="cookie-toggle-analytics"
                checked={analytics}
                onCheckedChange={setAnalytics}
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-xl border border-ink/10 p-4">
              <div>
                <p className="text-sm font-semibold text-ink">{t("cookie.marketing")}</p>
                <p className="mt-1 text-xs text-mineral">{t("cookie.marketingDesc")}</p>
              </div>
              <Switch
                data-testid="cookie-toggle-marketing"
                checked={marketing}
                onCheckedChange={setMarketing}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <button
              type="button"
              data-testid="cookie-reject-all-modal"
              onClick={rejectAll}
              className="rounded-full border border-ink/15 px-4 py-2.5 text-sm font-medium text-ink hover:bg-ink/5 transition-colors"
            >
              {t("cookie.rejectAll")}
            </button>
            <button
              type="button"
              data-testid="cookie-save-preferences"
              onClick={savePrefs}
              className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-terracotta transition-colors"
            >
              {t("cookie.save")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
